import { useState, useMemo, useCallback } from 'react';
import { useBulkConfig } from './use-bulk-config';
import { useSettingsData } from './use-settings-data';
import { buildCfg, buildPayload } from '../utils/settings-helpers';
import { DEFAULTS } from './settings-defaults';

export type Cfg = {
  payment_cycle_days: number;
  payment_threshold: number;
  payment_day_of_week: number;
  minimum_payout_banned: number;
  minimum_leads_count: number;
  minimum_leads_period: string;
  bonus_period: string;
  bonus_1: number;
  bonus_2: number;
  bonus_3: number;
  ranking_period: string;
  weekly_goal: number;
  total_goal: number;
  group_bonus_amount: number;
  daily_minimum_leads: number;
  top_tier_threshold: number;
  top_tier_days_required: number;
  pre_ban_failed_days_threshold: number;
  pre_ban_grace_days: number;
  daily_first_to_x_enabled: boolean;
  daily_first_to_x_threshold: number;
  daily_first_to_x_amount_usd: number;
  weekly_first_to_x_enabled: boolean;
  weekly_first_to_x_threshold: number;
  weekly_first_to_x_amount_usd: number;
  monthly_first_to_x_enabled: boolean;
  monthly_first_to_x_threshold: number;
  monthly_first_to_x_amount_usd: number;
};

export type SettingsState = {
  cfg: Cfg;
  set: (k: keyof Cfg, v: string | number | boolean) => void;
  loading: boolean;
  submitGroup: (payloadKeys: string[]) => void;
  isPending: boolean;
};

function useServerCfg() {
  const { base, extra, loading } = useSettingsData();
  const key = JSON.stringify([base, extra]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cfg = useMemo(() => buildCfg(base, extra), [key]);
  return { cfg, loading };
}

export function useSettingsState(): SettingsState {
  const { cfg: serverCfg, loading } = useServerCfg();
  const [overrides, setOverrides] = useState<Partial<Cfg>>({});
  const save = useBulkConfig();

  const cfg: Cfg = useMemo(
    () => ({ ...DEFAULTS, ...serverCfg, ...overrides }),
    [serverCfg, overrides]
  );

  const set = useCallback(
    (k: keyof Cfg, v: string | number | boolean) =>
      setOverrides((o) => ({ ...o, [k]: v })),
    []
  );

  const submitGroup = useCallback(
    (keys: string[]) => {
      const full = buildPayload(cfg);
      const partial: Record<string, unknown> = {};
      for (const k of keys) if (k in full) partial[k] = full[k];
      save.mutate(partial, { onSuccess: () => setOverrides({}) });
    },
    [cfg, save]
  );

  return { cfg, set, loading, submitGroup, isPending: save.isPending };
}
